import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { landingPathFor } from '@/lib/landing';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Loader2, AlertCircle, LogIn, Camera, ArrowRight } from 'lucide-react';

/**
 * EmailConfirmed page - handles the Supabase email confirmation redirect
 * 
 * When a user clicks the confirmation link in their email:
 * 1. Supabase redirects them here with access_token in URL hash
 * 2. We show a confirmation message
 * 3. If they have a session, we redirect them to their intended destination
 * 4. If no session (different browser), show "Sign In to continue" button
 */
export default function EmailConfirmed() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  
  const [status, setStatus] = useState('checking'); // 'checking', 'confirmed', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  
  // Get the intended destination from URL or localStorage
  const returnToParam = searchParams.get('returnTo');
  const pendingProfileStr = localStorage.getItem('rc_pending_profile');
  const pendingProfile = pendingProfileStr ? JSON.parse(pendingProfileStr) : null;
  
  // Determine where to redirect after confirmation
  const getRedirectPath = () => {
    // 1. Check URL param
    if (returnToParam) {
      return decodeURIComponent(returnToParam);
    }
    // 2. Check pending profile from registration
    if (pendingProfile?.redirect_after) {
      return pendingProfile.redirect_after;
    }
    // 3. Check if there's a saved order draft
    try {
      const draft = JSON.parse(localStorage.getItem('rc_book_draft'));
      if (draft?.kind) return `/book?type=${draft.kind}&resume=1`;
    } catch (e) {}
    return null;
  };
  
  const redirectPath = landingPathFor(user, getRedirectPath(), '/welcome');
  const hasOrderIntent = redirectPath.includes('/order/') || redirectPath.includes('package=');

  useEffect(() => {
    // Check URL hash for Supabase tokens
    const hash = window.location.hash;
    
    if (hash && hash.includes('access_token')) {
      // Supabase has processed the confirmation
      setStatus('confirmed');
      
      // Clear the hash from URL (cleaner UX)
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } else if (isAuthenticated) {
      // User is already authenticated (same browser session)
      setStatus('confirmed');
    } else if (!authLoading) {
      // No hash and not authenticated - might be different browser
      setStatus('confirmed'); // Still show confirmed message
    }
  }, [isAuthenticated, authLoading]);

  // Auto-redirect when authenticated
  useEffect(() => {
    if (isAuthenticated && status === 'confirmed' && !authLoading) {
      // Small delay for user to see confirmation message
      const timer = setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, status, authLoading, navigate, redirectPath]);

  if (status === 'checking' || authLoading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
        <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#C6A14A] mx-auto mb-4" />
            <p className="text-[#E8E4DE]/60">Verifying your email...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
        <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
            <CardTitle className="text-2xl font-light text-white">
              Verification Failed
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60 mt-2">
              {errorMessage || 'The confirmation link may have expired.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]">
              <Link to="/register">Try Registering Again</Link>
            </Button>
            <Button asChild variant="outline" className="w-full border-white/10 text-[#E8E4DE]">
              <Link to="/login">Already have an account? Sign In</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Confirmed status
  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
      
      <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10" data-testid="email-confirmed-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-400" />
          </div>
          <CardTitle className="text-2xl font-light text-white">
            You're Confirmed!
          </CardTitle>
          <CardDescription className="text-[#E8E4DE]/80 mt-2">
            Your email has been verified successfully.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {isAuthenticated ? (
            // User is authenticated - auto-redirecting
            <div className="text-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-[#C6A14A] mx-auto mb-3" />
              <p className="text-[#E8E4DE]/60 text-sm">
                {hasOrderIntent ? 'Taking you back to your order...' : 'Redirecting...'}
              </p>
            </div>
          ) : (
            // User is not authenticated - different browser or session expired
            <>
              <div className="bg-[#050816]/50 rounded-lg p-4 border border-white/5">
                <p className="text-[#E8E4DE]/60 text-sm text-center">
                  {hasOrderIntent 
                    ? "Sign in to continue with your order. Your selections are saved."
                    : "Sign in to access your account."}
                </p>
              </div>
              
              <Button 
                asChild 
                className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] h-12"
                data-testid="continue-signin-btn"
              >
                <Link to={`/login?returnTo=${encodeURIComponent(redirectPath)}`}>
                  <LogIn className="h-4 w-4 mr-2" />
                  Sign In to Continue
                </Link>
              </Button>
              
              {hasOrderIntent && (
                <p className="text-center text-[#E8E4DE]/40 text-xs">
                  Your order details are saved and waiting for you
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
